/**
 * File import/export. Imports are untrusted: the size is checked before the
 * file is read, the JSON is parsed as data only (nothing in it is executed),
 * older format versions are upgraded step by step through MIGRATIONS, and the
 * result goes through the same validator as everything else.
 */
import { LIMITS, TRACK_FORMAT, TRACK_FORMAT_VERSION, type TrackDocument } from '../authoring/TrackDocument';
import { parseTrackDocument } from '../authoring/validate';

/**
 * Upgrade steps keyed by the version they upgrade *from*. Each takes a parsed
 * (still untrusted) object and returns the next version's shape. Format 1 is
 * the first published format, so there are no steps yet; add one here (and a
 * fixture test) whenever TRACK_FORMAT_VERSION is raised.
 */
export type Migration = (raw: Record<string, unknown>) => Record<string, unknown>;
export const MIGRATIONS: Readonly<Record<number, Migration>> = Object.freeze({});

export type ImportResult = { ok: true; doc: TrackDocument; migratedFrom: number | null } | { ok: false; errors: string[] };

/** Applies migrations to a parsed object until it reaches the current version. */
export function migrate(raw: unknown, migrations: Readonly<Record<number, Migration>> = MIGRATIONS, target = TRACK_FORMAT_VERSION): ImportResult {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return { ok: false, errors: ['document: must be a JSON object'] };
  let obj = raw as Record<string, unknown>;
  if (obj['format'] !== TRACK_FORMAT) return { ok: false, errors: [`format: not a Xernous track (expected "${TRACK_FORMAT}")`] };
  const from = obj['formatVersion'];
  if (typeof from !== 'number' || !Number.isInteger(from) || from < 1) return { ok: false, errors: [`formatVersion: ${String(from)} is not a format version`] };
  if (from > target) return { ok: false, errors: [`formatVersion: ${from} is newer than this build (reads up to ${target}); update Xernous to open it`] };
  for (let v = from; v < target; v++) {
    const step = migrations[v];
    if (!step) return { ok: false, errors: [`formatVersion: no upgrade from version ${v}`] };
    obj = { ...step(obj), formatVersion: v + 1 };
  }
  const parsed = parseTrackDocument(obj);
  return parsed.ok ? { ok: true, doc: parsed.doc, migratedFrom: from === target ? null : from } : parsed;
}

/** UTF-8 size of `text`, computed only when the character count alone cannot decide (a character is 1–3 bytes). */
function exceedsBytes(text: string, max: number): boolean {
  if (text.length > max) return true;
  if (text.length * 3 <= max) return false;
  return new TextEncoder().encode(text).length > max;
}

export function importTrackText(text: string, migrations?: Readonly<Record<number, Migration>>, target?: number): ImportResult {
  if (exceedsBytes(text, LIMITS.maxFileBytes)) return { ok: false, errors: [`file: larger than ${(LIMITS.maxFileBytes / 1e6).toFixed(1)} MB`] };
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (err: unknown) {
    return { ok: false, errors: [`file: not valid JSON (${err instanceof Error ? err.message : String(err)})`] };
  }
  return migrate(raw, migrations, target);
}

export async function importTrackFile(file: Blob): Promise<ImportResult> {
  // Refuse before reading so a huge file is never loaded into memory.
  if (file.size > LIMITS.maxFileBytes) return { ok: false, errors: [`file: ${(file.size / 1e6).toFixed(1)} MB is larger than the ${(LIMITS.maxFileBytes / 1e6).toFixed(1)} MB limit`] };
  return withoutAgentPermission(importTrackText(await file.text()));
}

/**
 * Letting an agent use the painting is the person's choice in this editor, so
 * a file from elsewhere never arrives with it already switched on.
 */
export function withoutAgentPermission(r: ImportResult): ImportResult {
  const paint = r.ok ? r.doc.editor.paint : undefined;
  if (!r.ok || !paint?.allowAgent) return r;
  const rest = { ...paint };
  delete rest.allowAgent;
  return { ...r, doc: { ...r.doc, editor: { ...r.doc.editor, paint: rest } } };
}

export function exportFileName(doc: TrackDocument): string {
  const slug = doc.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'track';
  return `${slug}.xtrack.json`;
}
